import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  ArrowRight,
  Download,
  RefreshCw,
  Settings2,
  ShieldCheck,
} from 'lucide-react';
import Spinner from '../../../components/layout/Spinner';
import AlertPopup from '../../../components/layout/AlertPopUp';
import AppShell from '../../../components/layout/AppShell';
// Removed getUserCreditsRemaining import
import { getDefaultCVIcon } from '../../../services/userService';
import { useUser } from '../../../hooks/useUser';
import { Helmet } from 'react-helmet';
import Sidebar from '../../../components/Application/SidebarComponent';
import {
  initiateBulkCVConversion,
  getCVConversionResults,
  downloadConvertedResumes,
} from '../../../services/JSONService';
import FileUpload from '../../../components/ui/FileUpload';

const getDownloadFormatLabel = (filename) => {
  if (!filename) return 'File';

  const extension = filename.split('.').pop()?.toUpperCase();
  if (!extension) return 'File';

  return extension;
};

const Application = () => {
  const { user, loading } = useUser();

  const [isProcessing, setProcessing] = useState(false);
  const [downloadUrl, setDownloadUrl] = useState(null);
  const [downloadFilename, setDownloadFilename] = useState('ConvertedCV.docx');
  const [popup, setPopup] = useState({ show: false, type: '', title: '', message: '' });

  const [resumeFile, setResumeFile] = useState(null);
  const [jobDescriptionFile, setJobDescriptionFile] = useState(null);

  const [creditsRemaining, setCreditsRemaining] = useState(user?.credits_remaining || 0);

  const [sidebarOpen, setSidebarOpen] = useState(false);

  const [cvIcon, setCvIcon] = useState(null);

  const [selectedTemplate, setSelectedTemplate] = useState('Generic');

  const [cvOptions, setCVOptions] = useState({
    mode: 'Strict',
    length: 'Regular',
    perspective: 'FirstPerson',
    sectionLimits: {
      experience: 5,
      project: 5,
      skill: 5,
    },
  });

  const pollingIntervalRef = useRef(null);

  const showPopup = useCallback((type, title, message) => {
    setPopup({ show: true, type, title, message });
  }, []);

  const hidePopup = useCallback(() => {
    setPopup({ show: false });
  }, []);

  useEffect(() => {
    setCreditsRemaining(user?.credits_remaining || 0);
  }, [user]);

  useEffect(() => {
    getDefaultCVIcon()
      .then((iconData) => {
        if (iconData && iconData.message !== 'No CV icon found for the user') {
          const iconObject = {
            preview: `data:${iconData.icon_mimetype};base64,${iconData.icon_data}`,
            name: iconData.icon_filename,
            mimetype: iconData.icon_mimetype,
          };
          setCvIcon(iconObject);
        } else {
          setCvIcon(null);
        }
      })
      .catch((error) => {
        console.error('Error obtaining user icon:', error);
        setCvIcon(null);
      });
  }, []);
  
  const openSidebar = useCallback(() => {
    setSidebarOpen(true);
  }, []);

  const closeSidebar = useCallback(() => {
    setSidebarOpen(false);
  }, []);

  const handleOptionChange = useCallback((newOptions) => {
    setCVOptions(newOptions);
  }, []);

  const handleReset = useCallback(() => {
    setProcessing(false);
    if (downloadUrl) {
      window.URL.revokeObjectURL(downloadUrl);
    }
    setDownloadUrl(null);
    setDownloadFilename('ConvertedCV.docx');
    setResumeFile(null);
    setJobDescriptionFile(null);

    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  }, [downloadUrl]);

  const handleResumeFileUpload = useCallback((file) => {
    setResumeFile(file);
    return Promise.resolve();
  }, []);

  const handleJobDescriptionFileUpload = useCallback((file) => {
    setJobDescriptionFile(file);
    return Promise.resolve();
  }, []);

  const handleCompleteCVConversion = useCallback(async () => {
    if (!resumeFile) {
      showPopup('error', 'Error', 'Please upload a CV before converting.');
      return;
    }

    setProcessing(true);

    try {
      const data = await initiateBulkCVConversion(
        [resumeFile],
        jobDescriptionFile,
        cvOptions.mode,
        cvOptions.perspective.replace('-', ''),
        cvOptions.length,
        cvOptions.sectionLimits.experience,
        cvOptions.sectionLimits.project,
        cvOptions.sectionLimits.skill,
        selectedTemplate
      );

      const taskId = data.task_id;

      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }

      pollingIntervalRef.current = setInterval(async () => {
        try {
          const pollResult = await getCVConversionResults(taskId);

          if (pollResult.status === 'Completed') {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;

            const { data: downloadResponse, filename } = await downloadConvertedResumes(
              taskId,
              cvIcon ? cvIcon.preview : null,
              cvIcon ? cvIcon.mimetype : null,
              cvIcon ? cvIcon.name : null,
              selectedTemplate
            );

            const downloadUrl = window.URL.createObjectURL(downloadResponse);
            setDownloadUrl(downloadUrl);
            setDownloadFilename(filename);
            setProcessing(false);

            // Decrement creditsRemaining by 1
            setCreditsRemaining((prevCredits) => prevCredits - 1);
          } else if (pollResult.status === 'Failed') {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
            setProcessing(false);
            showPopup('error', 'Error', 'CV Conversion failed. Please try again.');
          }
        } catch (error) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
          setProcessing(false);
          showPopup('error', 'Error', 'An error occurred while fetching CV conversion results.');
        }
      }, 4000);
    } catch (error) {
      console.error('Error in handleCompleteCVConversion:', error);
      showPopup('error', 'Error', `Failed to complete action: ${error.message}`);
      setProcessing(false);
    }
  }, [
    resumeFile,
    jobDescriptionFile,
    cvOptions,
    selectedTemplate,
    cvIcon,
    showPopup,
  ]);

  const handleDownload = useCallback(() => {
    if (downloadUrl && downloadFilename) {
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = downloadFilename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }, [downloadUrl, downloadFilename]);

  const renderTemplateDropdown = useCallback(() => {
    const allowedTemplates = ['Generic', ...(user?.allowed_templates || [])];

    if (allowedTemplates.length <= 1) {
      return (
        <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm font-medium text-darkGrey">
          Generic
        </div>
      );
    }

    return (
      <select
        value={selectedTemplate}
        onChange={(e) => setSelectedTemplate(e.target.value)}
        disabled={isProcessing || downloadUrl}
        className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-darkGrey shadow-sm transition-colors focus:border-primaryBlue focus:outline-none focus:ring-2 focus:ring-primaryBlue/20 disabled:cursor-not-allowed disabled:bg-slate-100"
      >
        {allowedTemplates.map((template) => (
          <option key={template} value={template}>
            {template}
          </option>
        ))}
      </select>
    );
  }, [user, selectedTemplate, isProcessing, downloadUrl]);

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Spinner />
      </div>
    );
  }

  return (
    <AppShell currentPage="Application" loginRequired={true}>
      <Helmet>
        <title>Convert CVs | CVHQ</title>
      </Helmet>

      <Sidebar
        open={sidebarOpen}
        onClose={closeSidebar}
        options={cvOptions}
        onOptionsChange={handleOptionChange}
        cvIcon={cvIcon}
      />

      <div className="min-h-screen bg-white">
        <div className="mx-auto max-w-5xl px-4 py-8 sm:px-6 lg:px-8">
          <div className="mb-5 space-y-2">
            <h1 className="text-3xl font-semibold tracking-tight text-darkGrey">Convert your resume</h1>
            <p className="max-w-2xl text-sm leading-6 text-slateGrey">
              Upload your resume and job description, then convert.
            </p>
          </div>

          <div className="mb-4 flex flex-wrap items-center gap-3">
            <div className="inline-flex items-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-darkGrey">
              <ShieldCheck className="h-4 w-4 text-primaryBlue" />
              Credits remaining: <span className="font-semibold">{creditsRemaining}</span>
            </div>
            <div className="min-w-[12rem] flex-1">
              {renderTemplateDropdown()}
            </div>
            <button
              className="inline-flex items-center justify-center rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-darkGrey transition-colors hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-primaryBlue focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
              onClick={openSidebar}
              disabled={isProcessing || downloadUrl}
            >
              <Settings2 className="mr-2 h-4 w-4" />
              Open styling
            </button>
          </div>

          <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="grid gap-5 lg:grid-cols-2">
              <FileUpload
                id="resume-upload"
                isResume={true}
                onFileUpload={handleResumeFileUpload}
                selectedFile={resumeFile}
                disabled={isProcessing || Boolean(downloadUrl)}
              />
              <FileUpload
                id="job-description-upload"
                isResume={false}
                onFileUpload={handleJobDescriptionFileUpload}
                selectedFile={jobDescriptionFile}
                disabled={isProcessing || Boolean(downloadUrl)}
              />
            </div>

            <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-center">
              {!downloadUrl ? (
                <button
                  onClick={handleCompleteCVConversion}
                  disabled={isProcessing}
                  className={`inline-flex items-center justify-center rounded-md px-4 py-2.5 text-sm font-medium text-white transition-colors ${
                    isProcessing
                      ? 'cursor-not-allowed bg-gray-400'
                      : 'bg-primaryBlue hover:bg-deepNavy focus:outline-none focus:ring-2 focus:ring-primaryBlue focus:ring-offset-2'
                  }`}
                >
                  {isProcessing ? (
                    <>
                      <Spinner size={5} className="mr-2" />
                      Processing
                    </>
                  ) : (
                    <>
                      <ArrowRight className="mr-2 h-4 w-4" />
                      Convert CV
                    </>
                  )}
                </button>
              ) : (
                <>
                  <button
                    onClick={handleDownload}
                    className="inline-flex items-center justify-center rounded-md bg-emerald-600 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2"
                  >
                    <Download className="mr-2 h-4 w-4" />
                    Download {getDownloadFormatLabel(downloadFilename)}
                  </button>
                  <button
                    onClick={handleReset}
                    className="inline-flex items-center justify-center rounded-md border border-slate-300 bg-white px-4 py-2.5 text-sm font-medium text-darkGrey transition-colors hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-primaryBlue focus:ring-offset-2"
                  >
                    <RefreshCw className="mr-2 h-4 w-4" />
                    Start over
                  </button>
                </>
              )}
            </div>

            {isProcessing && (
              <div className="mt-4 flex items-center gap-3 rounded-lg bg-slate-50 px-4 py-3 text-sm text-slateGrey">
                <Spinner size={5} />
                Generating your file. This can take a moment.
              </div>
            )}
          </section>
        </div>
      </div>

      {popup.show && (
        <AlertPopup message={popup.message} type={popup.type} onClose={hidePopup} />
      )}
    </AppShell>
  );
};

export default Application;


